import { zodResolver } from '@hookform/resolvers/zod';
import { JSX, useEffect } from 'react';
import { useForm } from 'react-hook-form';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ItemStatus } from '@/modules/common';

import { TextStyleFormSchema, TextStyleFormValues } from '../validations';

interface TextStyleFormProps {
  editMode?: boolean;
  defaultValues?: TextStyleFormValues;
  onSubmit: (data: TextStyleFormValues) => Promise<void>;
  children: JSX.Element;
}

const defaultFormValues: TextStyleFormValues = {
  name: '',
  content: '',
  updatedAt: undefined,
  status: undefined,
};

export function TextStyleForm({
  children,
  onSubmit,
  editMode,
  defaultValues,
}: TextStyleFormProps) {
  const form = useForm<TextStyleFormValues>({
    resolver: zodResolver(TextStyleFormSchema),
    defaultValues: defaultFormValues,
  });

  useEffect(() => {
    if (defaultValues) {
      form.reset(defaultValues);
    }
  }, [defaultValues]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        {children}
        <div className="flex flex-col gap-10 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-4 gap-6 sm:grid-cols-2 grid-flow-col grid-rows-6 sm:grid-rows-1">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Add text style name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="updatedAt"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Last modified</FormLabel>
                  <FormControl>
                    <Input
                      disabled
                      placeholder="YYYY-MM-DD"
                      required={false}
                      readOnly
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <ItemStatus
              name="status"
              isDisabled={!editMode}
              control={form.control}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <FormField
              control={form.control}
              name="content"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Content</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Enter text style content"
                      maxLength={5000}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
      </form>
    </Form>
  );
}
